import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Alert, Form, Row, Col, Button, InputGroup, Modal
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const UAEInventoryTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities }) => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
  processingActivityName: '',
  dataType: '',
  classification: 'Personal Data',
  owner: '',
  dataSubjectCategory: '',
  processorThirdParty: '',
  purpose: '',
  retention: '',
  location: '',
  encryptedAtRest: false,
  encryptedInTransit: false,
  legalBasis: '',
  dataSubjects: []
});

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  useEffect(() => {
    fetchInventoryItems();
  }, []);

  const fetchInventoryItems = async () => {
    try {
      const res = await axios.get('https://dz51kxs2b1.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/inventory/list', { headers });
      setInventoryItems(res.data);
      calculateComplianceScore(res.data);
    } catch (error) {
      console.error('Error fetching UAE inventory:', error);
    }
  };

  const calculateComplianceScore = (items) => {
    if (items.length === 0) {
      onScoreUpdate(0);
      return;
    }

    // UAE PDPL compliance scoring
    const fullyCompliant = items.filter(item => 
     item.processingActivityName &&
item.dataType &&
item.classification &&
item.owner &&
item.dataSubjectCategory &&
item.processorThirdParty &&
item.purpose &&
item.legalBasis &&
item.retention &&
item.location
    ).length;

    // Additional UAE-specific compliance checks
    const encryptedItems = items.filter(item => 
      item.encryptedAtRest && item.encryptedInTransit
    ).length;

    const sensitiveDataCompliant = items.filter(item => {
      if (item.classification === 'Sensitive Personal Data') {
        return item.encryptedAtRest && item.encryptedInTransit && 
               (item.legalBasis === 'Explicit Consent' || 
                item.legalBasis === 'Medical Treatment' ||
                item.legalBasis === 'Legal Obligation');
      }
      return true;
    }).length;

    // Calculate weighted score
    const basicComplianceScore = (fullyCompliant / items.length) * 60;
    const encryptionScore = (encryptedItems / items.length) * 25;
    const sensitiveDataScore = (sensitiveDataCompliant / items.length) * 15;

    const score = Math.round(basicComplianceScore + encryptionScore + sensitiveDataScore);
    onScoreUpdate(score);
  };

  const handleSave = async () => {
    try {
      const payload = { ...newItem };
      
      if (editingItem) {
        await axios.put(`https://agc81fprt5.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/inventory/update/${editingItem.id}`, payload, { headers });
        await logActivity(`Updated UAE inventory item: ${payload.dataType}`, "warning");
      } else {
        await axios.post('https://70sxpaun41.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/inventory/create', payload, { headers });
        await logActivity(`Added new UAE inventory item: ${payload.dataType}`, "success");
      }

      showNotification('UAE data inventory item saved successfully!');
      fetchInventoryItems();
      resetForm();
    } catch (error) {
      console.error('Error saving UAE inventory item:', error);
      showNotification('Error saving inventory item', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this data inventory item?')) {
      try {
        await axios.delete(`https://9d6i6853q0.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/inventory/delete/${id}`, { headers });
        await logActivity(`Deleted UAE inventory item`, "danger");
        showNotification('UAE data inventory item deleted successfully!');
        fetchInventoryItems();
      } catch (error) {
        console.error('Error deleting UAE inventory item:', error);
        showNotification('Error deleting inventory item', 'danger');
      }
    }
  };

  const resetForm = () => {
    setNewItem({
      dataType: '',
      classification: 'Personal Data',
      owner: '',
      purpose: '',
      retention: '',
      location: '',
      encryptedAtRest: false,
      encryptedInTransit: false,
      legalBasis: '',
      dataSubjects: []
    });
    setEditingItem(null);
    setShowModal(false);
  };

  const logActivity = async (message, badge = "info") => {
    try {
      const payload = {
        action: badge,
        module: "UAE Data Inventory",
        details: message
      };
      
      const response = await axios.post(
        "https://6x7bayihnj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activity-log/save",
        payload,     
        { headers }
      );
      
      if (refreshActivities) {
        await refreshActivities();
      }
    } catch (err) {
      console.error('❌ Error logging UAE inventory activity:', err);
      
      if (err.response) {
        console.error('🔥 Server Error Details:');
        console.error('Status:', err.response.status);
        console.error('Data:', err.response.data);
      }
      
      console.warn("Failed to log UAE activity:", err.message);
    }
  };

  return (
    <>
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>UAE Personal Data Inventory Management</Accordion.Header>
          <Accordion.Body>
            <Alert variant="info" className="mb-3">
              <strong>UAE PDPL Compliance:</strong> Under Federal Decree-Law No. 45 of 2021, organizations must maintain 
              detailed records of all personal data processing activities. This includes data types, purposes, legal basis, 
              storage locations, and security measures implemented.
            </Alert>

            <Row className="mb-3">
              <Col>
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <FaPlus /> Add New Personal Data Asset
                </Button>
              </Col>
            </Row>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                <th>Processing Activity</th>
<th>Data Type</th>
                  <th>Classification</th>
                  <th>Data Controller</th>
                  <th>Data Subjects</th>
<th>Processor / Third Party</th>
                  <th>Processing Purpose</th>
                  <th>Legal Basis</th>
                  <th>Retention Period</th>
                  <th>Storage Location</th>
                  <th>Encryption Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventoryItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.dataType}</td>
                    <td>{item.processingActivityName || '-'}</td>
                    <td>
                      <Badge bg={
                        item.classification === 'Sensitive Personal Data' ? 'danger' :
                        item.classification === 'Biometric Data' ? 'dark' :
                        item.classification === 'Personal Data' ? 'warning' : 'info'
                      }>
                        {item.classification}
                      </Badge>
                    </td>
                    <td>{item.owner}</td>
                    <td>{item.dataSubjectCategory || '-'}</td>
<td>{item.processorThirdParty || '-'}</td>
                    <td>{item.purpose}</td>
                    <td>{item.legalBasis}</td>
                    <td>{item.retention}</td>
                    <td>
                      {item.location}
                      {item.location && item.location.toLowerCase().includes('uae') && (
                        <Badge bg="success" className="ms-1">Local</Badge>
                      )}
                    </td>
                    <td>
                      <small>
                        At Rest: {item.encryptedAtRest ? <FaCheck className="text-success" /> : <FaTimes className="text-danger" />}<br/>
                        In Transit: {item.encryptedInTransit ? <FaCheck className="text-success" /> : <FaTimes className="text-danger" />}
                      </small>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-warning" className="me-1" 
                              onClick={() => { setEditingItem(item); setNewItem(item); setShowModal(true); }}>
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingItem ? 'Edit' : 'Add'} Personal Data Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
           
           <Row>
  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Processing Activity Name</Form.Label>
      <Form.Control
        type="text"
        value={newItem.processingActivityName}
        onChange={(e) =>
          setNewItem({ ...newItem, processingActivityName: e.target.value })
        }
        placeholder="e.g., Customer Onboarding, Employee Management"
      />
    </Form.Group>
  </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Personal Data Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={newItem.dataType}
                    onChange={(e) => setNewItem({...newItem, dataType: e.target.value})}
                    placeholder="e.g., Customer Email Addresses, Employee Emirates ID"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Classification</Form.Label>
                  <Form.Select
                    value={newItem.classification}
                    onChange={(e) => setNewItem({...newItem, classification: e.target.value})}
                  >
                    <option value="Personal Data">Personal Data</option>
                    <option value="Sensitive Personal Data">Sensitive Personal Data</option>
                    <option value="Biometric Data">Biometric Data</option>
                    <option value="Pseudonymized Data">Pseudonymized Data</option>
                    <option value="Anonymized Data">Anonymized Data</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Data Subject Category</Form.Label>
      <Form.Select
        value={newItem.dataSubjectCategory}
        onChange={(e) =>
          setNewItem({ ...newItem, dataSubjectCategory: e.target.value })
        }
      >
        <option value="">Select Data Subject</option>
        <option value="Customers">Customers</option>
        <option value="Employees">Employees</option>
        <option value="Children">Children</option>
        <option value="Vendors">Vendors</option>
        <option value="Website Visitors">Website Visitors</option>
        <option value="Partners">Partners</option>
      </Form.Select>
    </Form.Group>
  </Col>
</Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Controller</Form.Label>
                  <Form.Control
                    type="text"
                    value={newItem.owner}
                    onChange={(e) => setNewItem({...newItem, owner: e.target.value})}
                    placeholder="Department or individual responsible"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Legal Basis for Processing</Form.Label>
                  <Form.Select
                    value={newItem.legalBasis}
                    onChange={(e) => setNewItem({...newItem, legalBasis: e.target.value})}
                  >
                    <option value="">Select Legal Basis</option>
                    <option value="Explicit Consent">Explicit Consent</option>
                    <option value="Contractual Necessity">Contractual Necessity</option>
                    <option value="Legal Obligation">Legal Obligation under UAE Law</option>
                    <option value="Vital Interests">Vital Interests Protection</option>
                    <option value="Public Interest">Public Interest Task</option>
                    <option value="Legitimate Interest">Legitimate Interest</option>
                    <option value="Employment Purpose">Employment Purpose</option>
                    <option value="Medical Treatment">Medical Treatment & Health Services</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Processor / Third Party</Form.Label>
      <Form.Control
        type="text"
        value={newItem.processorThirdParty}
        onChange={(e) =>
          setNewItem({ ...newItem, processorThirdParty: e.target.value })
        }
        placeholder="Example: Internal Only, AWS, Microsoft, Salesforce"
      />
    </Form.Group>
  </Col>
</Row>
            <Form.Group className="mb-3">
              <Form.Label>Processing Purpose</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newItem.purpose}
                onChange={(e) => setNewItem({...newItem, purpose: e.target.value})}
                placeholder="Describe the specific purpose for processing this personal data under UAE PDPL"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Retention Period</Form.Label>
                  <Form.Control
                    type="text"
                    value={newItem.retention}
                    onChange={(e) => setNewItem({...newItem, retention: e.target.value})}
                    placeholder="e.g., 5 years (financial records), 7 years (tax records)"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Storage Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={newItem.location}
                    onChange={(e) => setNewItem({...newItem, location: e.target.value})}
                    placeholder="e.g., UAE Data Center, Dubai Cloud Zone, Abu Dhabi"
                  />
                  <Form.Text className="text-muted">
                    Specify exact location as required by UAE data localization requirements
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Encrypted at Rest (AES-256 recommended)"
                  checked={newItem.encryptedAtRest}
                  onChange={(e) => setNewItem({...newItem, encryptedAtRest: e.target.checked})}
                />
                <Form.Text className="text-muted">
                  Required for sensitive personal data under UAE PDPL
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Encrypted in Transit (TLS 1.3 recommended)"
                  checked={newItem.encryptedInTransit}
                  onChange={(e) => setNewItem({...newItem, encryptedInTransit: e.target.checked})}
                />
                <Form.Text className="text-muted">
                  Mandatory for all personal data transfers under UAE PDPL
                </Form.Text>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            {editingItem ? 'Update' : 'Create'} Data Asset
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UAEInventoryTab;
